import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { consigneeKeys } from './keys';

/**
 * Keeps every open tab's view of the consignee master current.
 *
 * A broker often has the Master page in one tab and a draft in another. When
 * the master changes in one, the other should re-read it without a reload, so
 * the draft's Importir card can re-plan its fills and suggestions. Every master
 * mutation calls `broadcastConsigneeChange()`; pages that read the master mount
 * `useConsigneeLiveInvalidation()`, which invalidates the whole key family.
 *
 * BroadcastChannel is the transport. Where it is missing (older Safari), a
 * localStorage key bump plus the `storage` event does the same job: that event
 * fires in every other tab of the origin, never in the tab that wrote.
 */

export const CONSIGNEE_LIVE_CHANNEL = 'jarvis-consignee-master';

/**
 * Identifies this tab. BroadcastChannel delivers to every other channel
 * instance, including one in the same tab, and the tab that changed the
 * master has already invalidated its own cache; its own message is ignored.
 */
const THIS_TAB_ID = `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
export const CONSIGNEE_LIVE_STORAGE_KEY = 'jarvis-consignee-master:changed';

type ChannelLike = {
  postMessage: (message: unknown) => void;
  close: () => void;
  onmessage: ((event: MessageEvent) => void) | null;
};

type ChannelCtor = new (name: string) => ChannelLike;

export interface ConsigneeLiveEnv {
  BroadcastChannel?: ChannelCtor;
  storage?: Pick<Storage, 'setItem'>;
  /** Registers a `storage` event listener; returns the remover. */
  addStorageListener?: (listener: (event: { key: string | null }) => void) => () => void;
  now?: () => number;
  /** Defaults to this tab's id; tests pass distinct ids to play two tabs. */
  tabId?: string;
}

export const defaultConsigneeLiveEnv = (): ConsigneeLiveEnv => {
  const env: ConsigneeLiveEnv = {};
  if (typeof BroadcastChannel !== 'undefined') {
    env.BroadcastChannel = BroadcastChannel as unknown as ChannelCtor;
  }
  if (typeof window !== 'undefined') {
    try {
      env.storage = window.localStorage;
    } catch {
      // Storage blocked (private mode, policy): no fallback transport.
    }
    env.addStorageListener = (listener) => {
      const handler = (event: StorageEvent) => listener({ key: event.key });
      window.addEventListener('storage', handler);
      return () => window.removeEventListener('storage', handler);
    };
  }
  return env;
};

export type BroadcastResult = 'channel' | 'storage' | 'none';

/** Tells every other tab that the consignee master changed. Never throws. */
export const broadcastConsigneeChange = (env: ConsigneeLiveEnv = defaultConsigneeLiveEnv()): BroadcastResult => {
  const at = (env.now ?? Date.now)();
  if (env.BroadcastChannel) {
    try {
      const channel = new env.BroadcastChannel(CONSIGNEE_LIVE_CHANNEL);
      channel.postMessage({ type: 'consignee-master-changed', at, source: env.tabId ?? THIS_TAB_ID });
      channel.close();
      return 'channel';
    } catch {
      // Fall through to storage.
    }
  }
  if (env.storage) {
    try {
      env.storage.setItem(CONSIGNEE_LIVE_STORAGE_KEY, String(at));
      return 'storage';
    } catch {
      // Quota or policy: nothing else to try.
    }
  }
  return 'none';
};

/** Calls `listener` whenever another tab broadcasts a change. Returns the unsubscriber. */
export const subscribeConsigneeChange = (
  listener: () => void,
  env: ConsigneeLiveEnv = defaultConsigneeLiveEnv()
): (() => void) => {
  if (env.BroadcastChannel) {
    try {
      const channel = new env.BroadcastChannel(CONSIGNEE_LIVE_CHANNEL);
      const ownTabId = env.tabId ?? THIS_TAB_ID;
      channel.onmessage = (event: MessageEvent) => {
        const source = (event?.data as { source?: string } | undefined)?.source;
        if (source && source === ownTabId) return;
        listener();
      };
      return () => {
        channel.onmessage = null;
        channel.close();
      };
    } catch {
      // Fall through to storage.
    }
  }
  if (env.addStorageListener) {
    return env.addStorageListener((event) => {
      if (event.key === CONSIGNEE_LIVE_STORAGE_KEY) listener();
    });
  }
  return () => {};
};

/** Mount once per page that reads the consignee master. */
export function useConsigneeLiveInvalidation(): void {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      subscribeConsigneeChange(() => {
        void queryClient.invalidateQueries({ queryKey: consigneeKeys.all });
      }),
    [queryClient]
  );
}
